import { useCallback, useState } from 'react';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { registerQuery, useMutation, useQuery } from 'data-fetching-client';
import { ReplyIcon } from 'foundations-assets/icons/components/ReplyIcon';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Section } from 'foundations-components/transitional/components/Section';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { createStack } from 'hub-http';
import promiseClient from 'hub-http/adapters/promiseClient';
import * as coreMiddlewares from 'hub-http/middlewares/core';
import noAuthStack from 'hub-http/stacks/noAuthWithCredentialsHubapi';
import HelpfulButton from 'marketplace-ui-reviews/components/HelpfulButton';
import Rating from 'marketplace-ui-reviews/components/Rating';
import ReportButton from 'marketplace-ui-reviews/components/ReportButton';
import ReviewActionsWrapper from 'marketplace-ui-reviews/components/ReviewActionsWrapper';
import ReviewBody from 'marketplace-ui-reviews/components/ReviewBody';
import ReviewEditToggle from 'marketplace-ui-reviews/components/ReviewEditToggle';
import ReviewReplyThread from 'marketplace-ui-reviews/components/ReviewReplyThread';
import ReviewTranslationToggle from 'marketplace-ui-reviews/components/ReviewTranslationToggle';
import ReviewerInfo from 'marketplace-ui-reviews/components/ReviewerInfo';
import ReportReviewModal from 'marketplace-ui-reviews/components/modals/ReportReviewModal';
import ReviewReplyModal from 'marketplace-ui-reviews/components/modals/ReviewReplyModal';
import { RatingAxes } from 'marketplace-ui-reviews/constants/RatingAxes';
import { REPORT_REPLY } from 'marketplace-ui-reviews/queries/reportReplyQuery';
import { REPORT_REVIEW } from 'marketplace-ui-reviews/queries/reportReviewQuery';
import { SET_REVIEW_HELPFUL } from 'marketplace-ui-reviews/queries/setReviewHelpfulQuery';
import { SUBMIT_REPLY } from 'marketplace-ui-reviews/queries/submitReplyQuery';
import { getUserMarkedHelpful } from 'marketplace-ui-reviews/utils/getUserMarkedHelpful';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const REPLY_LIMIT = 2;
const serviceCatalogPublicClient = promiseClient(createStack(coreMiddlewares.base('service-catalog/public/v1/'), noAuthStack));
export const GET_CATALOG_SERVICES = registerQuery({
  fieldName: 'catalogServices',
  args: [],
  fetcher() {
    return serviceCatalogPublicClient.get('services');
  }
});
export default function ReviewRenderer({
  appContext = 'public',
  borderBottom,
  review,
  showActions = true,
  showReplyAction = false,
  searchTerm,
  reviewType,
  onTrackInteraction,
  userId
}) {
  const isInApp = appContext === 'app';
  const helpfulCount = review.getHelpfulCount();
  const userMarkedHelpful = getUserMarkedHelpful(userId ? userId.toString() : '', review);
  const {
    data
  } = useQuery(GET_CATALOG_SERVICES, {
    skip: reviewType !== 'PROFILE'
  });
  const catalogServices = data === null || data === void 0 ? void 0 : data.catalogServices;
  const [setIsHelpful] = useMutation(SET_REVIEW_HELPFUL);
  const [reportReview] = useMutation(REPORT_REVIEW);
  const [reportReply] = useMutation(REPORT_REPLY);
  const [submitReply] = useMutation(SUBMIT_REPLY);
  const [showOriginal, setShowOriginal] = useState(false);
  const [showUntranslatedText, setShowUntranslatedText] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showReportReplyModal, setShowReportReplyModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [reportingReplyId, setReportingReplyId] = useState(null);
  const [userMarkedHelpfulState, setUserMarkedHelpfulState] = useState(userMarkedHelpful);
  const [helpfulCountState, setHelpfulCountState] = useState(helpfulCount);
  const replyDisabled = review.getReplies(showUntranslatedText).length >= REPLY_LIMIT;
  const showReviewEditToggle = review.get('lastEditedAt') && review.get('original').ratings.get('OVERALL') !== review.get('ratings').get('OVERALL');
  const ratingValue = showOriginal ? review.get('original').ratings.get(RatingAxes.OVERALL) : review.ratings.get(RatingAxes.OVERALL);
  const onReportModalConfirm = useCallback(({
    reason,
    comment
  }) => {
    return new Promise((resolve, reject) => {
      reportReview({
        variables: {
          reviewId: review.get('id'),
          reportReason: reason,
          comment
        }
      }).then(() => {
        setShowReportModal(false);
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }, [reportReview, review]);
  const onReportReplyModalConfirm = useCallback(({
    reason,
    comment
  }) => {
    return new Promise((resolve, reject) => {
      if (reportingReplyId === null) {
        reject(new Error('No reply selected for reporting'));
        return;
      }
      reportReply({
        variables: {
          reviewId: review.get('id'),
          replyId: reportingReplyId,
          reportReason: reason,
          comment
        }
      }).then(() => {
        setShowReportReplyModal(false);
        setReportingReplyId(null);
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }, [reportReply, review, reportingReplyId]);
  const onReplyModalConfirm = useCallback(({
    reply
  }) => {
    return new Promise((resolve, reject) => {
      submitReply({
        variables: {
          reviewId: review.get('id'),
          reply
        }
      }).then(() => {
        setShowReplyModal(false);
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }, [submitReply, review]);
  const onClickHelpful = useCallback(() => {
    return new Promise((resolve, reject) => {
      setIsHelpful({
        variables: {
          reviewId: review.get('id'),
          isHelpful: !userMarkedHelpfulState
        }
      }).then(() => {
        setUserMarkedHelpfulState(!userMarkedHelpfulState);
        if (userMarkedHelpfulState) {
          setHelpfulCountState(helpfulCountState - 1);
        } else {
          setHelpfulCountState(helpfulCountState + 1);
        }
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }, [review, userMarkedHelpfulState, setIsHelpful, helpfulCountState]);
  return /*#__PURE__*/_jsxs(View, {
    as: "div",
    inlineSize: "100%",
    paddingBlock: "800",
    borderBlockEndStyle: borderBottom ? 'solid' : undefined,
    borderBlockEndWidth: borderBottom ? '100' : undefined,
    borderBlockEndColor: borderBottom ? 'border-core-subtle' : undefined,
    children: [/*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(Rating, {
        direction: "column",
        label: /*#__PURE__*/_jsxs(List, {
          inline: true,
          use: "inline-divided",
          children: [/*#__PURE__*/_jsx(FormattedDateTime, {
            value: review.get('createdAt'),
            format: "ll",
            type: "userTz"
          }), review.get('lastEditedAt') && /*#__PURE__*/_jsxs(View, {
            as: "span",
            marginInlineStart: "100",
            children: [/*#__PURE__*/_jsx(View, {
              as: "span",
              marginInlineEnd: "100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.reviewsCard.edited"
              })
            }), /*#__PURE__*/_jsx(FormattedDateTime, {
              value: review.get('lastEditedAt'),
              format: "ll",
              type: "userTz"
            })]
          })]
        }),
        rating: ratingValue,
        showTextRating: true
      })
    }), /*#__PURE__*/_jsx(ReviewBody, {
      catalogServices: catalogServices,
      review: review,
      searchTerm: searchTerm,
      showOriginal: showOriginal,
      showUntranslatedText: showUntranslatedText,
      reviewType: reviewType,
      onTrackInteraction: onTrackInteraction
    }), /*#__PURE__*/_jsx(ReviewerInfo, {
      review: review,
      inline: true
    }), showActions && /*#__PURE__*/_jsxs(ReviewActionsWrapper, {
      itemRight: /*#__PURE__*/_jsx(ReviewTranslationToggle, {
        review: review,
        onTrackInteraction: onTrackInteraction,
        showUntranslatedText: showUntranslatedText,
        setShowUntranslatedText: setShowUntranslatedText
      }),
      children: [/*#__PURE__*/_jsx(ListItem, {
        children: showReplyAction ? /*#__PURE__*/_jsx(Tooltip, {
          disabled: !replyDisabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.reviewsCard.replyLimit"
          }),
          children: /*#__PURE__*/_jsxs(Button, {
            "data-test-id": "review-reply-button",
            disabled: replyDisabled,
            use: "tertiary-light",
            size: "sm",
            onClick: () => {
              setShowReplyModal(true);
            },
            children: [/*#__PURE__*/_jsx(ReplyIcon, {
              size: "300"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.reviewsCard.reply"
            })]
          })
        }) : /*#__PURE__*/_jsx(HelpfulButton, {
          disabled: !isInApp,
          helpfulCount: helpfulCountState,
          onClickHelpful: onClickHelpful,
          userMarkedHelpful: userMarkedHelpfulState
        })
      }), /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(ReportButton, {
          isCustomer: isInApp,
          onClick: () => setShowReportModal(true)
        })
      }), showReviewEditToggle && /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(ReviewEditToggle, {
          review: review,
          showOriginal: showOriginal,
          setShowOriginal: setShowOriginal
        })
      })]
    }), review.getReplies(showUntranslatedText).map(reply => /*#__PURE__*/_jsx(ReviewReplyThread, {
      onReportReplyClick: isInApp ? () => {
        setReportingReplyId(reply.get('id'));
        setShowReportReplyModal(true);
      } : undefined,
      reply: reply
    }, reply.get('id'))), showReportModal && /*#__PURE__*/_jsx(ReportReviewModal, {
      onClose: () => setShowReportModal(false),
      onConfirm: onReportModalConfirm,
      review: review,
      type: 'REVIEW'
    }), showReportReplyModal && /*#__PURE__*/_jsx(ReportReviewModal, {
      catalogServices: catalogServices,
      onClose: () => {
        setShowReportReplyModal(false);
        setReportingReplyId(null);
      },
      onConfirm: onReportReplyModalConfirm,
      review: review,
      type: 'REPLY'
    }), showReplyModal && /*#__PURE__*/_jsx(ReviewReplyModal, {
      catalogServices: catalogServices,
      onClose: () => setShowReplyModal(false),
      onConfirm: onReplyModalConfirm,
      review: review,
      ReviewRenderer: ReviewRenderer,
      reviewRendererProps: {
        showActions: false
      }
    })]
  });
}